import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  Card,
  CardContent,
  CardActions,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { apiRequest } from '../utils/api';

interface TypeOfWork {
  id: number;
  workName: string;
  description: string | null;
  isActive: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

export default function TypeOfWorkPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [items, setItems] = useState<TypeOfWork[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const [openDialog, setOpenDialog] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [form, setForm] = useState({
    workName: '',
    description: '',
  });

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TypeOfWork | null>(null);

  const showError = (message: string) =>
    setSnackbar({ open: true, message, severity: 'error' });
  const showSuccess = (message: string) =>
    setSnackbar({ open: true, message, severity: 'success' });

  const loadItems = async () => {
    setLoading(true);
    try {
      const response = await apiRequest('/type-of-work', { method: 'GET' });
      const data = (response.data || []) as TypeOfWork[];
      setItems(data);
    } catch (error) {
      console.error('Error loading type of work:', error);
      showError(
        error instanceof Error ? error.message : 'Failed to load type of work'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const handleOpenCreate = () => {
    setIsEdit(false);
    setSelectedId(null);
    setForm({
      workName: '',
      description: '',
    });
    setOpenDialog(true);
  };

  const handleOpenEdit = (item: TypeOfWork) => {
    setIsEdit(true);
    setSelectedId(item.id);
    setForm({
      workName: item.workName,
      description: item.description || '',
    });
    setOpenDialog(true);
  };

  const handleSave = async () => {
    if (!form.workName) {
      showError('Work Name is required');
      return;
    }

    setSaving(true);
    try {
      if (!isEdit) {
        // Create new type of work
        const response = await apiRequest('/type-of-work', {
          method: 'PUT',
          body: JSON.stringify({
            workName: form.workName,
            description: form.description || null,
            isActive: true,
          }),
        });
        const data = response.data as { typeOfWork: TypeOfWork };
        setItems((prev) => [data.typeOfWork, ...prev]);
        showSuccess('Type of work created');
      } else if (selectedId != null) {
        // Update type of work
        const response = await apiRequest(`/type-of-work/${selectedId}`, {
          method: 'POST',
          body: JSON.stringify({
            workName: form.workName,
            description: form.description || null,
          }),
        });
        const data = response.data as { typeOfWork: TypeOfWork };
        setItems((prev) =>
          prev.map((it) => (it.id === data.typeOfWork.id ? data.typeOfWork : it))
        );
        showSuccess('Type of work updated');
      }
      setOpenDialog(false);
    } catch (error) {
      console.error('Error saving type of work:', error);
      showError(
        error instanceof Error ? error.message : 'Failed to save type of work'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDelete = (item: TypeOfWork) => {
    setDeleteTarget(item);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setSaving(true);
      await apiRequest(`/type-of-work/${deleteTarget.id}`, { method: 'DELETE' });
      setItems((prev) => prev.filter((it) => it.id !== deleteTarget.id));
      showSuccess('Type of work deleted');
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
    } catch (error) {
      console.error('Error deleting type of work:', error);
      showError(
        error instanceof Error ? error.message : 'Failed to delete type of work'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const displayedItems = items.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="overline" color="text.secondary">
            Settings
          </Typography>
          <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" sx={{ fontWeight: 600 }}>
            Type of Work
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={loadItems} title="Refresh">
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            size={isMobile ? 'small' : 'medium'}
            sx={{ bgcolor: 'grey.900', '&:hover': { bgcolor: 'grey.800' } }}
          >
            {isMobile ? 'Add' : 'New Type of Work'}
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        /* ── Mobile card list ── */
        <Box>
          {displayedItems.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ mt: 4 }}>
              No type of work found
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {displayedItems.map((item, index) => (
                <Card key={item.id} variant="outlined">
                  <CardContent sx={{ pb: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {item.workName}
                      </Typography>
                      <Chip label={`#${index + 1 + page * rowsPerPage}`} size="small" />
                    </Box>
                    {item.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {item.description}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.disabled">
                      {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : ''}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                    <IconButton size="small" onClick={() => handleOpenEdit(item)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleOpenDelete(item)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </CardActions>
                </Card>
              ))}
            </Box>
          )}
          <TablePagination
            component="div"
            count={items.length}
            rowsPerPageOptions={[5, 10, 25]}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ mt: 1 }}
          />
        </Box>
      ) : (
        /* ── Desktop table ── */
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Work Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Updated At</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No type of work found
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedItems.map((item, index) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{index + 1 + page * rowsPerPage}</TableCell>
                      <TableCell>{item.workName}</TableCell>
                      <TableCell>{item.description || '-'}</TableCell>
                      <TableCell>
                        {item.createdAt
                          ? new Date(item.createdAt).toLocaleString()
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {item.updatedAt
                          ? new Date(item.updatedAt).toLocaleString()
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton onClick={() => handleOpenEdit(item)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            onClick={() => handleOpenDelete(item)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={items.length}
            rowsPerPageOptions={[5, 10, 25, 50]}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>{isEdit ? 'Edit Type of Work' : 'New Type of Work'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Work Name"
              value={form.workName}
              onChange={(e) => setForm({ ...form, workName: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              fullWidth
              multiline
              minRows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !form.workName}
          >
            {saving ? <CircularProgress size={24} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Type of Work</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{' '}
            <strong>{deleteTarget?.workName}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={saving}
          >
            {saving ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
